import { ApiValidationError } from './errors';

/**
 * True for an HTTP failure that never reached the server (offline, DNS, CORS, a
 * dropped connection): Angular reports those as ``status === 0``. An
 * ``ApiValidationError`` always carries a 4xx status, so it is never one.
 */
export function isNetworkError(err: unknown): boolean {
  return (
    !!err &&
    typeof err === 'object' &&
    !(err instanceof ApiValidationError) &&
    (err as { status?: unknown }).status === 0
  );
}

/**
 * The one user-facing "no connection" message. ``errorInterceptor`` shows it for a
 * status-0 failure; a caller that opts out of that toast with ``silentErrorContext()``
 * (because it renders failures itself) must show the same wording, not its generic
 * "Save failed." — a dropped connection is something the user can fix and retry.
 */
export function networkErrorMessage(): string {
  return $localize`:@@errors.network:Connection problem. Check your network and retry.`;
}

/**
 * Pull a human-readable message out of an unknown HTTP error.
 *
 * The backend can return several shapes and until this file existed each
 * caller reached into ``err.error.detail`` (or nothing) and dropped the
 * rest on the floor. That's why users saw "Operation failed." /
 * "Something went wrong on the server" / "Could not enable account"
 * banners while the network tab held the real message.
 *
 * Precedence (highest → lowest):
 *   1. ``err.error.detail`` — DRF top-level ("Not found.", "Authentication...")
 *   2. ``err.error.error_details`` — 500 fallback populated by
 *      ``apps/core/exception_handler.global_exception_handler`` with the
 *      unhandled exception's ``ValidationError: ['This password is too
 *      common.']`` string
 *   3. ``err.error.error`` string with an optional code map (mobile_exists,
 *      demo_cap_exceeded, ...). Unknown codes fall through to the raw code
 *      so at least the operator sees something specific.
 *   4. ``err.error.non_field_errors[0]``
 *   5. Flatten every string / string[] leaf in the body and join with " · "
 *      (handles ``{"field": ["msg"]}``, nested block errors like
 *      ``{"student": {"mobile": ["..."]}}`` and the list-of-dicts a
 *      ``many=True`` child serializer returns, e.g.
 *      ``{"entries": [{}, {"session_count": ["..."]}]}`` -> ``entries.session_count: ...``;
 *      identical messages are reported once)
 *   6. ``err.message`` (network / thrown Error)
 *   7. ``fallback`` (localized generic)
 *
 * An ``ApiValidationError`` (what ``errorInterceptor`` throws for every 400/422)
 * is unwrapped to the response it carries first: it has no ``.error`` of its own,
 * so without that the caller would only ever see "Validation failed".
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;
  if (err instanceof ApiValidationError) {
    return extractApiErrorMessage(err.originalResponse, fallback);
  }
  const body = (err as { error?: unknown }).error;
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;

    const detail = typeof obj['detail'] === 'string' ? obj['detail'] : null;
    // The 500 fallback shape (apps/core/exception_handler.py) also sets
    // `detail = "An unexpected server error occurred."` and packs the real
    // exception into `error_details`. When both are present, prefer the
    // details — the generic detail hides what actually broke.
    const errorDetails =
      typeof obj['error_details'] === 'string' ? (obj['error_details'] as string) : null;
    if (errorDetails && detail === 'An unexpected server error occurred.') {
      return prettifyErrorDetails(errorDetails);
    }
    if (detail) return detail;

    const code = typeof obj['error'] === 'string' ? obj['error'] : null;
    if (code) {
      const mapped = mapKnownCode(code, obj);
      if (mapped) return mapped;
      return code;
    }

    const nonField = obj['non_field_errors'];
    if (Array.isArray(nonField) && nonField.length && typeof nonField[0] === 'string') {
      return nonField[0];
    }

    const leaves = flattenLeaves(obj);
    if (leaves.length) return leaves.join(' · ');

    if (errorDetails) return prettifyErrorDetails(errorDetails);
  }
  const msg = (err as { message?: unknown }).message;
  // Skip Angular's default HttpErrorResponse message ("Http failure
  // response for /path: 500 Server Error") — it's not user-friendly and
  // callers pass a localized fallback for exactly this case.
  if (typeof msg === 'string' && msg && !msg.startsWith('Http failure')) return msg;
  return fallback;
}

/**
 * ``error_details`` typically arrives as ``"ValidationError: ['This password
 * is too common.']"``. Strip the exception name + the Python list wrapping
 * so the message reads cleanly.
 */
function prettifyErrorDetails(raw: string): string {
  const stripPrefix = raw.replace(/^\w+(Error|Exception):\s*/, '');
  const listMatch = stripPrefix.match(/^\[(['"])(.+?)\1(?:,\s*(['"]).+?\3)*\]$/);
  if (listMatch && listMatch[2]) return listMatch[2];
  return stripPrefix;
}

function mapKnownCode(code: string, body: Record<string, unknown>): string | null {
  switch (code) {
    case 'mobile_exists':
      return $localize`:@@errors.mobile_exists:A student with this phone number is already registered.`;
    case 'demo_cap_exceeded':
      return $localize`:@@errors.demo_cap_exceeded:Demo cap exceeded for this student.`;
    case 'already_registered_for_competition':
      return $localize`:@@errors.already_in_competition:Student is already registered for this competition.`;
    case 'missing_field':
      return $localize`:@@errors.missing_field:A required field is missing.`;
    case 'group_at_capacity':
      return $localize`:@@errors.group_at_capacity:Group is at capacity.`;
    case 'already_in_active_group':
      return $localize`:@@errors.already_in_active_group:Student is already in an active group.`;
    case 'exam_not_open':
      return $localize`:@@errors.exam_not_open:This exam is not open right now.`;
    case 'subscription_already_active':
      return $localize`:@@errors.subscription_already_active:This student already has an active subscription for this group. Cancel or let it end before renewing.`;
    case 'invalid_attempt_state': {
      // Backend payload: {error, current_status, expected}. When the
      // "expected" side names a specific requirement (e.g. "ready (with at
      // least one ExamQuestion)") show it — proctors see what's actually
      // missing instead of a bare "wrong state".
      const current = typeof body['current_status'] === 'string' ? body['current_status'] : '';
      const expected = typeof body['expected'] === 'string' ? body['expected'] : '';
      if (current && expected) {
        return $localize`:@@errors.invalid_attempt_state_detail:Attempt is in "${current}:CURRENT:" state (expected "${expected}:EXPECTED:"). Please contact a proctor.`;
      }
      return $localize`:@@errors.invalid_attempt_state:The attempt is in an unexpected state. Please contact a proctor.`;
    }
    case 'clock_sync_failed': {
      const reason = typeof body['reason'] === 'string' ? body['reason'] : '';
      if (reason) {
        return $localize`:@@errors.clock_sync_failed_detail:Clock sync failed: ${reason}:REASON:. Please refresh and retry.`;
      }
      return $localize`:@@errors.clock_sync_failed:Clock sync failed. Please refresh and retry.`;
    }
    case 'timing_tamper_detected': {
      const detail = typeof body['detail'] === 'string' ? body['detail'] : '';
      const base = $localize`:@@errors.timing_tamper:Timing integrity check failed. Your attempt has been flagged. Please contact a proctor immediately.`;
      return detail ? `${base} (${detail})` : base;
    }
    default:
      return null;
  }
}

/** Keys that are consumed by earlier branches of the precedence chain. */
const TOP_LEVEL_SKIP = new Set(['detail', 'reason', 'error', 'error_details', 'non_field_errors']);
/** Deepest nesting walked; real DRF bodies are at most object -> list -> object -> list. */
const MAX_DEPTH = 4;

function flattenLeaves(obj: Record<string, unknown>): string[] {
  const out: string[] = [];
  collectLeaves(obj, '', 0, out);
  // A many=True serializer repeats the same message once per bad row; say it once.
  return [...new Set(out)];
}

/**
 * Walk a DRF error body collecting ``path: message`` strings.
 *
 * Handles ``"a": "msg"``, ``"a": ["msg"]``, ``"a": {"b": ["msg"]}`` and
 * ``"a": [{}, {"b": ["msg"]}]`` (list of per-row dicts; rows without errors are ``{}``
 * and contribute nothing). Nested paths are joined with ``.`` — the list index is
 * dropped on purpose, the row isn't meaningful to the person reading the toast.
 */
function collectLeaves(value: unknown, path: string, depth: number, out: string[]): void {
  if (typeof value === 'string') {
    if (path && value) out.push(`${path}: ${value}`);
    return;
  }
  if (Array.isArray(value)) {
    const strings = value.filter((x): x is string => typeof x === 'string' && x !== '');
    if (path && strings.length) out.push(`${path}: ${strings.join(' ')}`);
    if (depth >= MAX_DEPTH) return;
    for (const item of value) {
      if (item && typeof item === 'object') collectLeaves(item, path, depth + 1, out);
    }
    return;
  }
  if (value && typeof value === 'object' && depth < MAX_DEPTH) {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (depth === 0 && TOP_LEVEL_SKIP.has(key)) continue;
      collectLeaves(child, path ? `${path}.${key}` : key, depth + 1, out);
    }
  }
}
